import { Spinner } from 'admin-components/components';
import { Column, Page, Row } from 'components/shared/Layout';
import VoucherConfirmModal from 'components/shared/VoucherConfirmModal';
import Button from 'components/ui/Button';
import { ACTION_CODES } from 'constants/errors';

import { replaceVoucherErrorCodes, voucherCodes } from 'constants/vouchers';
import AgentPortalError from 'errors/AgentPortalError';
import moment from 'moment';
import { path } from 'ramda';
import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useForm, FormProvider } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import useManualVoucherCreation from 'components/mutations/useManualVoucherCreation';
import { GET_USER } from 'store/queries';
import { useManagedQuery } from 'utils/managedHooks';
import REPLACE_VOUCHER from './mutations/replaceVoucher';
import RecipientDetails from './RecipientDetails';
import VoucherDetails from './VoucherDetails';

const oneYearFromToday = moment().add(1, 'year').format('YYYY-MM-DD').toString();

const CreateVoucher = () => {
  const { push } = useHistory();
  const [cachedFormData, cacheFormData] = useState({});
  const [isReplaceConfirmationOpened, setReplaceConfirmationOpened] = useState(false);
  const {
    data: {
      me: { allowedVoucherCreationTypes, canManageVouchers, paymentCurrencies }
    }
  } = useManagedQuery(GET_USER, {
    fetchPolicy: 'cache-only'
  });

  if (!canManageVouchers) {
    push('/');
  }

  const [createVoucher, { loading }] = useManualVoucherCreation();
  const [replaceVoucherMutation, { loading: replaceVoucherLoading, error: replaceVoucherError }] = useMutation(REPLACE_VOUCHER);

  const formMethods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange'
  });

  const { watch, handleSubmit, formState } = formMethods;
  const { errors } = formState;

  const { voucherType, currency, amount, voucherToReplace } = watch();

  const onSubmit = async (formData) => {
    cacheFormData(formData);

    if (formData.voucherType === voucherCodes.REV) {
      setReplaceConfirmationOpened(true);

      return;
    }

    const {
      data: {
        createVoucher: { alias }
      }
    } = await createVoucher({
      variables: {
        amount: parseFloat(formData.amount),
        type: formData.voucherType,
        validUntil: formData.salesDateEnd,
        currency: formData.currency,
        recipientEmail: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        language: formData.language,
        ...(formData.associatedPNR && { associatedPNR: formData.associatedPNR })
      }
    });

    push(`/vouchers/${alias}`);
  };

  const onConfirmReplaceSubmit = async ({ reason }) => {
    try {
      const {
        data: {
          replaceVoucher: { alias }
        }
      } = await replaceVoucherMutation({
        variables: {
          amount: parseFloat(amount),
          reason,
          voucherToReplace
        }
      });
      push(`/vouchers/${alias}`);
    } catch (err) {
      setReplaceConfirmationOpened(false);
      const replaceVoucherErrorCode = replaceVoucherErrorCodes[path(['graphQLErrors', [0], 'code'], err)];
      if (!replaceVoucherErrorCode) {
        throw new AgentPortalError(err, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
          errorCTARedirect: '/vouchers/create'
        });
      }
    }
  };

  if (loading) {
    return (
      <Row height="100vh">
        <Column justifyContent="center" alignItems="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <Page columns={20}>
        <Column span={18} start={2}>
          <Row>
            <h1>
              <FormattedMessage id="createVoucher.generateVoucher" />
            </h1>
          </Row>

          <VoucherConfirmModal
            isOpen={isReplaceConfirmationOpened}
            isLoading={replaceVoucherLoading}
            onToggle={() => setReplaceConfirmationOpened(!isReplaceConfirmationOpened)}
            onSubmit={onConfirmReplaceSubmit}
          />
          <form onSubmit={handleSubmit(onSubmit)}>
            <VoucherDetails
              errors={{
                amount: !!errors?.amount,
                associatedPNR: !!errors?.associatedPNR,
                voucherToReplace: !!errors?.voucherToReplace,
                voucherToReplaceAPIError: replaceVoucherError
              }}
              allowedVoucherTypes={allowedVoucherCreationTypes}
              allowedCurrencies={paymentCurrencies}
              voucherType={voucherType}
              currency={currency}
              oneYearFromToday={oneYearFromToday}
              defaultValues={{
                voucherType: cachedFormData.voucherType,
                currency: cachedFormData.currency || paymentCurrencies[0],
                amount: cachedFormData.amount,
                salesDateEnd: cachedFormData.salesDateEnd,
                associatedPNR: cachedFormData.associatedPNR
              }}
            />
            {voucherType !== voucherCodes.REV && (
              <RecipientDetails
                errors={{
                  firstName: !!errors?.firstName,
                  lastName: !!errors?.lastName,
                  email: !!errors?.email
                }}
                defaultValues={{
                  firstName: cachedFormData.firstName,
                  lastName: cachedFormData.lastName,
                  email: cachedFormData.email,
                  language: cachedFormData.language
                }}
              />
            )}
            <Column>
              <Button type="submit" data-cy="submit" disabled={loading} loading={loading}>
                <FormattedMessage id="createVoucher.generateVoucher" />
              </Button>
            </Column>
          </form>
        </Column>
      </Page>
    </FormProvider>
  );
};

export default CreateVoucher;
